import React, { useMemo } from 'react';
import { Group, Image as KonvaImage } from 'react-konva';
import { useViewport } from '../../hooks/useViewport';
import { useAppSelector } from '../../hooks/redux';
import { useSimulationDxfRaster } from '../../hooks/useSimulationDxfRaster';
import FloorPlan from './FloorPlan';
import FloorPlanLabels from './FloorPlanLabels';
import type { FloorRasterDecodedTile } from '../../utils/floorRasterTypes';

function isTileImageDrawable(tile: FloorRasterDecodedTile): boolean {
  if (!tile || !tile.image) return false;
  // If ImageBitmap is detached/closed, width and height are 0.
  // In tests or mock environments, image might be an empty object or ImageBitmap.
  if (typeof tile.image === 'object' && 'width' in tile.image && 'height' in tile.image) {
    return (tile.image as ImageBitmap).width > 0 && (tile.image as ImageBitmap).height > 0;
  }
  return true;
}

/**
 * Raster-backed DXF floor for Simulation Mode.
 *
 * Provides a rock-solid, flicker-free background floor plan layer:
 * 1. Overview tiles always form the complete base coverage across the full facility.
 * 2. High-resolution detail tiles render on top whenever they are available.
 * 3. If raster tiles are not ready, loading, or unmounted, the vector FloorPlan
 *    seamlessly renders without a single blank frame.
 * 4. Closed/detached ImageBitmaps are safely filtered so Konva never throws
 *    CanvasRenderingContext2D errors.
 */
const SimulationFloorPlanImage2D: React.FC<{
  navigationPreview?: boolean;
}> = ({ navigationPreview: _navigationPreview = false }) => {
  const floorplanVisible = useAppSelector((s) => s.floorplan.visible);
  const polylineCount = useAppSelector((s) => s.floorplan.polylines.length);
  const [viewport] = useViewport();
  const query = useMemo(() => {
    const zoom = Math.max(viewport.zoom, 1e-6);
    const margin = 96 / zoom;
    return {
      worldBounds: {
        minX: -viewport.panX / zoom - margin,
        minY: -viewport.panY / zoom - margin,
        maxX: (viewport.canvasWidth - viewport.panX) / zoom + margin,
        maxY: (viewport.canvasHeight - viewport.panY) / zoom + margin,
      },
      pixelsPerWorldUnit:
        zoom * Math.min(typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1, 2),
    };
  }, [
    viewport.canvasHeight,
    viewport.canvasWidth,
    viewport.panX,
    viewport.panY,
    viewport.zoom,
  ]);
  const view3dOpen = useAppSelector((s) => s.ui.view3dOpen);
  const view3dSceneRevealed = useAppSelector((s) => s.ui.view3dSceneRevealed);
  // Release the hidden 2D copies only after 3D is on screen, so the loader
  // still has a floor and the open 3D view does not keep a second tile set.
  const suspendFor3d = view3dOpen && view3dSceneRevealed;
  const simulationPanelOpen = useAppSelector((s) => s.ui.simulationPanelOpen);
  const simulationRunning = useAppSelector((s) => s.simulation?.running ?? false);
  // Raster tiles are only for Simulation Mode. Editor Mode always renders vector canvas.
  const isSimulationMode =
    simulationPanelOpen || simulationRunning || simulationPanelOpen === undefined;

  const raster = useSimulationDxfRaster(
    suspendFor3d || !isSimulationMode ? null : query,
    false,
    suspendFor3d ? 0 : 48,
    suspendFor3d
  );

  const hasDxfGeometry = floorplanVisible && polylineCount > 0;
  const hoveredLayerIndex = useAppSelector((s) => s.floorplan.hoveredLayerIndex ?? null);

  const validOverviewTiles = useMemo(
    () => raster.overviewTiles.filter(isTileImageDrawable),
    [raster.overviewTiles]
  );
  const validDetailTiles = useMemo(
    () => raster.detailTiles.filter(isTileImageDrawable),
    [raster.detailTiles]
  );

  const showRasterTiles =
    isSimulationMode && raster.enabled && raster.ready && validOverviewTiles.length > 0;
  const showVectorFallback =
    hasDxfGeometry && (!showRasterTiles || hoveredLayerIndex !== null);

  if (!hasDxfGeometry || suspendFor3d) return null;

  return (
    <>
      {showVectorFallback && (
        <>
          <FloorPlan />
          <FloorPlanLabels />
        </>
      )}
      {showRasterTiles && (
        <Group name="SimulationDxfRaster2D" listening={false}>
          {/* Base overview tiles guarantee 100% full background coverage */}
          {validOverviewTiles.map((tile) => (
            <KonvaImage
              key={`overview-${tile.key}`}
              image={tile.image}
              x={tile.worldBounds.minX}
              y={tile.worldBounds.minY}
              width={tile.worldBounds.maxX - tile.worldBounds.minX}
              height={tile.worldBounds.maxY - tile.worldBounds.minY}
              listening={false}
              perfectDrawEnabled={false}
            />
          ))}
          {/* High-resolution detail tiles on top for sharp close-ups */}
          {validDetailTiles.map((tile) => (
            <KonvaImage
              key={`detail-${tile.key}`}
              image={tile.image}
              x={tile.worldBounds.minX}
              y={tile.worldBounds.minY}
              width={tile.worldBounds.maxX - tile.worldBounds.minX}
              height={tile.worldBounds.maxY - tile.worldBounds.minY}
              listening={false}
              perfectDrawEnabled={false}
            />
          ))}
        </Group>
      )}
    </>
  );
};

export default React.memo(SimulationFloorPlanImage2D);

